<template>
  <div class="select-wrapper">
    <Dropdown
      v-if="!optionGroupLabel"
      v-model="currentValue"
      class="col-12 p-0"
      :options="dropdownOptions"
      option-label="label"
      option-value="value"
      option-disabled="disabled"
      :disabled="disabled"
      :placeholder="placeholder"
      :filter="filter"
      @change="emitChangedValue"
    >
      <template
        v-if="hasCustomOptions"
        #option="slotProps"
      >
        <div class="flex align-items-center justify-content-between w-full">
          <div class="flex align-items-center gap-2">
            <i
              v-if="slotProps.option.isCustom"
              class="fa-solid fa-puzzle-piece custom-icon"
            />
            <span>{{ slotProps.option.label }}</span>
          </div>
          <span
            v-if="slotProps.option.isCustom"
            class="custom-badge-inline"
          >
            CUSTOM
          </span>
        </div>
      </template>
    </Dropdown>
    <Dropdown
      v-else
      v-model="currentValue"
      class="col-12 p-0"
      :options="optionGroupLabel ? options : dropdownOptions"
      option-label="label"
      option-value="value"
      option-disabled="disabled"
      :option-group-label="optionGroupLabel"
      :option-group-children="optionGroupChildren"
      :disabled="disabled"
      :placeholder="placeholder"
      :filter="filter"
      @change="emitChangedValue"
    >
      <template
        v-if="$slots.optiongroup"
        #optiongroup="slotProps"
      >
        <slot
          name="optiongroup"
          :option="slotProps.option"
        />
      </template>
    </Dropdown>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  Ref,
  ref,
  watch,
} from 'vue';

import { DropdownOption } from '@/types';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';

export default defineComponent({
  name: 'BaseDropdown',

  components: {
    Dropdown,
  },

  props: {
    modelValue: {
      type: [String, Number],
      required: true,
    },

    options: {
      type: [Array, Object] as PropType<string[] | DropdownOption[]>,
      required: true,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    placeholder: {
      type: String,
      required: false,
      default: '',
    },

    optionGroupLabel: {
      type: String,
      required: false,
      default: '',
    },

    optionGroupChildren: {
      type: String,
      required: false,
      default: '',
    },

    filter: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['update:modelValue'],

  setup(props, context) {
    const { t } = useI18n();
    // Initialiser avec un tableau vide pour éviter les erreurs de typage
    const dropdownOptions: Ref<DropdownOption[]> = ref([]);
    const currentValue = ref(props.modelValue);

    const emitChangedValue = () => {
      context.emit('update:modelValue', currentValue.value);
    };

    watch(() => props.options, (newValue) => {
      if (!newValue) {
        dropdownOptions.value = [];
        return;
      }

      // Si c'est un dropdown groupé, ne pas transformer les options
      if (props.optionGroupLabel) {
        return;
      }

      dropdownOptions.value = newValue.map((option: string | DropdownOption) => {
        // Si l'option est une chaîne simple
        if (typeof option === 'string') {
          return { value: option, label: option };
        }

        // Si l'option est un objet avec value/label
        if (typeof option === 'object' && option !== null) {
          // Vérifier si l'option.label est une clé de traduction ou une valeur directe
          let label = typeof option.label === 'string' && option.label.startsWith('customData.') ? t(option.label) : option.label;
          if (typeof option.label === 'string' && label !== t(option.label)) {
            label = t(option.label);
          }
          const mapped: any = { value: option.value, label, disabled: option.disabled };
          if ((option as any).isCustom) mapped.isCustom = true;
          return mapped;
        }

        return { value: '', label: '' };
      });
    }, { immediate: true });

    const hasCustomOptions = ref(false);
    watch(dropdownOptions, (opts) => {
      hasCustomOptions.value = opts.some((o: any) => o.isCustom);
    }, { immediate: true });

    watch(() => props.modelValue, () => {
      currentValue.value = props.modelValue;
    });

    return {
      dropdownOptions,
      hasCustomOptions,
      currentValue,
      emitChangedValue,
    };
  },
});
</script>
